import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, GraduationCap, DollarSign,
  ClipboardList, Briefcase, Library, Bus, Home,
  Settings, LogOut, Bell, Menu, BookOpen,
  BarChart3, Globe, Languages, Search, ChevronDown, Sun, Moon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { initials } from '../../lib/utils';
import { useAuthStore } from '../../stores/authStore';
import { useModuleStore } from '../../stores/moduleStore';
import { api } from '../../lib/api';
import { toast } from '../../stores/notificationStore';
import { setLanguage, getLanguage } from '../../lib/i18n';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  module?: string;
  children?: { label: string; href: string }[];
}

interface NavGroup {
  group: string;
  items: NavItem[];
}

function useNavGroups(): NavGroup[] {
  const { t } = useTranslation('common');
  return [
    {
      group: 'Overview',
      items: [
        { label: t('nav.dashboard'), href: '/dashboard', icon: LayoutDashboard },
      ],
    },
    {
      group: 'Academic',
      items: [
        { label: t('nav.students'),  href: '/students',  icon: Users,        module: 'academic' },
        { label: t('nav.academics'), href: '/academic',  icon: GraduationCap, module: 'academic',
          children: [
            { label: t('nav.classesStreams'), href: '/academic/classes' },
            { label: t('nav.attendance'),    href: '/academic/attendance' },
            { label: t('nav.timetable'),     href: '/academic/timetable' },
          ],
        },
        { label: t('nav.exams'), href: '/exams', icon: ClipboardList, module: 'academic',
          children: [
            { label: t('nav.examSchedule'), href: '/exams' },
            { label: t('nav.gradeEntry'),   href: '/exams/grades' },
            { label: t('nav.reportCards'),  href: '/exams/report-cards' },
          ],
        },
      ],
    },
    {
      group: 'Finance',
      items: [
        { label: t('nav.finance'), href: '/finance', icon: DollarSign, module: 'finance',
          children: [
            { label: t('nav.invoices'),           href: '/finance/invoices' },
            { label: t('nav.payments'),           href: '/finance/payments' },
            { label: t('nav.feeStructures'),      href: '/finance/fee-structures' },
            { label: t('nav.defaulters'),         href: '/finance/defaulters' },
            { label: t('nav.bankReconciliation'), href: '/finance/reconciliation' },
            { label: t('nav.expenses'),           href: '/finance/expenses' },
            { label: t('nav.vat'),                href: '/finance/vat' },
          ],
        },
      ],
    },
    {
      group: 'HR',
      items: [
        { label: t('nav.hr'), href: '/hr', icon: Briefcase, module: 'hr',
          children: [
            { label: t('nav.staff'),       href: '/hr/staff' },
            { label: t('nav.leave'),       href: '/hr/leave' },
            { label: t('nav.payrollRuns'), href: '/hr/payroll' },
          ],
        },
      ],
    },
    {
      group: 'Management',
      items: [
        { label: t('nav.library'),   href: '/library',   icon: Library,  module: 'library' },
        { label: t('nav.hostel'),    href: '/hostel',    icon: Home,     module: 'hostel' },
        { label: t('nav.transport'), href: '/transport', icon: Bus,      module: 'transport' },
        { label: t('nav.assets'),    href: '/assets',    icon: BookOpen, module: 'assets' },
        { label: t('nav.government'), href: '/government', icon: Globe, module: 'government_reporting',
          children: [
            { label: t('nav.emis'),  href: '/government/emis' },
            { label: t('nav.uneb'),  href: '/government/uneb' },
            { label: t('nav.ubteb'), href: '/government/ubteb' },
          ],
        },
        { label: t('nav.analytics'), href: '/analytics', icon: BarChart3, module: 'analytics_basic',
          children: [
            { label: t('nav.dashboards'), href: '/analytics' },
            { label: t('nav.aiInsights'), href: '/analytics/ai' },
          ],
        },
      ],
    },
    {
      group: 'Administration',
      items: [
        { label: 'Users', href: '/users', icon: Users },
      ],
    },
    {
      group: 'Support',
      items: [
        { label: t('nav.settings'), href: '/settings', icon: Settings },
      ],
    },
  ];
}

function NavLink({ item }: { item: NavItem }) {
  const location = useLocation();
  const isEnabled = useModuleStore(s => s.isEnabled);
  const [open, setOpen] = useState(false);

  if (item.module && !isEnabled(item.module)) return null;

  const isActive = location.pathname.startsWith(item.href);
  const Icon = item.icon;

  const itemStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: active ? 500 : 400,
    cursor: 'pointer',
    transition: 'all 0.12s',
    color: active ? '#fff' : 'rgba(255,255,255,0.55)',
    background: active ? 'var(--p600)' : 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left' as const,
    textDecoration: 'none',
  });

  if (item.children) {
    return (
      <div>
        <button
          onClick={() => setOpen(o => !o)}
          style={itemStyle(isActive)}
          onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
          onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          <Icon
            size={15}
            strokeWidth={isActive ? 2.2 : 1.8}
            style={{ opacity: isActive ? 1 : 0.6, flexShrink: 0 }}
          />
          <span style={{ flex: 1 }}>{item.label}</span>
          <ChevronDown
            size={11}
            style={{
              opacity: 0.5,
              transition: 'transform 0.15s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </button>
        {open && (
          <div style={{ marginLeft: 24, marginTop: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {item.children.map(child => {
              const childActive = location.pathname === child.href;
              return (
                <Link
                  key={child.href}
                  to={child.href}
                  style={{
                    display: 'block',
                    padding: '6px 10px',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: childActive ? 500 : 400,
                    color: childActive ? '#fff' : 'rgba(255,255,255,0.45)',
                    textDecoration: 'none',
                    transition: 'all 0.12s',
                  }}
                  onMouseEnter={e => { if (!childActive) (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.75)'; }}
                  onMouseLeave={e => { if (!childActive) (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.45)'; }}
                >
                  {child.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.href}
      style={itemStyle(isActive)}
      onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
      onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
    >
      <Icon
        size={15}
        strokeWidth={isActive ? 2.2 : 1.8}
        style={{ opacity: isActive ? 1 : 0.6, flexShrink: 0 }}
      />
      <span>{item.label}</span>
    </Link>
  );
}

function SidebarContent({ onClose }: { onClose?: () => void }) {
  const { user, tenant, clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const navGroups = useNavGroups();

  async function handleLogout() {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    clearAuth();
    navigate('/login');
    toast('success', 'Logged out successfully');
    onClose?.();
  }

  const schoolInitial = (tenant?.name ?? 'U').charAt(0).toUpperCase();

  return (
    <div style={{
      width: 'var(--sidebar-w)',
      background: 'var(--p900)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
    }}>
      {/* Logo section */}
      <div style={{ padding: '20px 16px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'var(--p600)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 18, fontWeight: 700, color: '#fff',
          }}>
            {schoolInitial}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {tenant?.name ?? 'USMS'}
            </div>
            <div style={{ fontSize: 10, color: 'var(--p200)', marginTop: 1, textTransform: 'capitalize' }}>
              {tenant?.pricing_tier ?? 'School Dashboard'}
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
        {navGroups.map(group => (
          <div key={group.group} style={{ marginBottom: 4 }}>
            <div style={{
              fontSize: 9, fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.1em',
              color: 'var(--p400)',
              padding: '10px 8px 3px',
            }}>
              {group.group}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {group.items.map(item => (
                <NavLink key={item.href} item={item} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div style={{ padding: '12px 10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: 'var(--p600)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 600, color: '#fff', flexShrink: 0,
          }}>
            {user ? initials(user.first_name, user.last_name) : '?'}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.first_name} {user?.last_name}
            </div>
            <div style={{ fontSize: 10, color: 'var(--p400)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.role?.replace(/_/g, ' ')}
            </div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 12, color: 'rgba(255,255,255,0.45)',
            padding: '4px 2px',
            transition: 'color 0.12s',
          }}
          onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.75)')}
          onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.45)')}
        >
          <LogOut size={13} />
          Sign out
        </button>
      </div>
    </div>
  );
}

function usePageTitle() {
  const location = useLocation();
  const { t } = useTranslation('common');
  const map: Record<string, string> = {
    '/dashboard':            t('nav.dashboard'),
    '/students':             t('nav.students'),
    '/academic':             t('nav.academics'),
    '/academic/classes':     t('nav.classesStreams'),
    '/academic/attendance':  t('nav.attendance'),
    '/academic/timetable':   t('nav.timetable'),
    '/exams':                t('nav.exams'),
    '/exams/grades':         t('nav.gradeEntry'),
    '/exams/report-cards':   t('nav.reportCards'),
    '/finance':              t('nav.finance'),
    '/finance/invoices':     t('nav.invoices'),
    '/finance/payments':     t('nav.payments'),
    '/finance/fee-structures': t('nav.feeStructures'),
    '/finance/defaulters':   t('nav.defaulters'),
    '/finance/reconciliation': t('nav.bankReconciliation'),
    '/finance/expenses':     t('nav.expenses'),
    '/finance/vat':          t('nav.vat'),
    '/hr':                   t('nav.hr'),
    '/hr/staff':             t('nav.staff'),
    '/hr/leave':             t('nav.leave'),
    '/hr/payroll':           t('nav.payrollRuns'),
    '/library':              t('nav.library'),
    '/hostel':               t('nav.hostel'),
    '/transport':            t('nav.transport'),
    '/assets':               t('nav.assets'),
    '/government':           t('nav.government'),
    '/government/emis':      t('nav.emis'),
    '/government/uneb':      t('nav.uneb'),
    '/government/ubteb':     t('nav.ubteb'),
    '/analytics':            t('nav.analytics'),
    '/analytics/ai':         t('nav.aiInsights'),
    '/settings':             t('nav.settings'),
  };
  return map[location.pathname] ?? 'USMS';
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lang, setLang] = useState<'en' | 'lg'>(getLanguage);
  const { t } = useTranslation('common');
  const pageTitle = usePageTitle();
  const { theme, setTheme } = useAuthStore();

  const today = new Date().toLocaleDateString('en-UG', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  function toggleLanguage() {
    const next = lang === 'en' ? 'lg' : 'en';
    setLanguage(next);
    setLang(next);
  }

  return (
    <div className="layout-root">
      {/* Desktop sidebar */}
      <div className="sidebar-desktop">
        <SidebarContent />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 50,
            background: 'rgba(38,33,92,0.5)',
            backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* Mobile sidebar panel */}
      <div style={{
        position: 'fixed',
        left: mobileOpen ? 0 : '-240px',
        top: 0, bottom: 0,
        zIndex: 51,
        transition: 'left 0.2s ease',
      }}>
        <SidebarContent onClose={() => setMobileOpen(false)} />
      </div>

      {/* Main column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>

        {/* Desktop topbar */}
        <header
          className="desktop-topbar"
          style={{
            height: 64, flexShrink: 0,
            background: 'var(--surface)',
            borderBottom: '1px solid var(--border)',
            alignItems: 'center',
            padding: '0 28px',
            gap: 16,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 22, fontWeight: 600,
              color: 'var(--text)',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
            }}>
              {pageTitle}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', letterSpacing: '0.01em', marginTop: 2 }}>
              {today}
            </div>
          </div>

          {/* Search placeholder */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--surface2)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '7px 14px',
            color: 'var(--text-3)', fontSize: 12, cursor: 'pointer', minWidth: 160,
          }}>
            <Search size={13} />
            <span>Search…</span>
          </div>

          {/* Language toggle */}
          <button
            onClick={toggleLanguage}
            title={t('language.switchTo', { lang: lang === 'en' ? 'Luganda' : 'English' })}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '7px 12px',
              fontSize: 12, fontWeight: 500, color: 'var(--text-2)',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            <Languages size={13} />
            {lang === 'en' ? 'LG' : 'EN'}
          </button>

          {/* Theme toggle */}
          <button
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            title={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
            style={{
              width: 36, height: 36, borderRadius: 8,
              background: 'var(--surface2)', border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s',
            }}
          >
            {theme === 'light' ? <Moon size={15} color="var(--text-2)" /> : <Sun size={15} color="var(--text-2)" />}
          </button>

          {/* Bell */}
          <button style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'var(--surface2)', border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', position: 'relative', flexShrink: 0,
          }}>
            <Bell size={16} color="var(--text-2)" />
          </button>
        </header>

        {/* Mobile topbar */}
        <div
          className="mobile-topbar"
          style={{
            background: 'var(--p900)',
            padding: '10px 16px',
            alignItems: 'center',
            gap: 10,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setMobileOpen(true)}
            style={{
              width: 36, height: 36, borderRadius: 8,
              background: 'rgba(255,255,255,0.1)',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Menu size={18} color="#fff" />
          </button>
          <div style={{
            width: 28, height: 28, borderRadius: 7,
            background: 'var(--p600)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 14, fontWeight: 600, color: '#fff',
          }}>
            U
          </div>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>USMS</span>
          <div style={{ flex: 1 }} />
          <button onClick={toggleLanguage} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 500 }}>
            {lang === 'en' ? 'LG' : 'EN'}
          </button>
        </div>

        {/* Page content */}
        <main style={{ flex: 1, padding: '0 28px 28px', overflowY: 'auto', background: 'var(--bg)' }}>
          {children}
        </main>

        {/* Footer */}
        <footer style={{
          textAlign: 'center', padding: '10px 24px',
          fontSize: 11, color: 'var(--text-3)',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface)',
          flexShrink: 0,
        }}>
          © {new Date().getFullYear()} USMS — Uganda School Management System
        </footer>
      </div>
    </div>
  );
}
